import type { SupportTicketComment } from '@support/entities/support-ticket';
import {
  useAddTicketComment,
  useTicketComments,
} from '@support/entities/support-ticket';
import { SUPPORT_COMMENT_MAX_LENGTH } from '@travel-planner/shared/schemas';
import { useId, useRef, useState } from 'react';
import { formatUtcDate } from '@/shared/lib/format-utc-date';
import { Button, Skeleton, Text, Textarea, toastStore } from '@/shared/ui';
import css from './ticket-conversation.module.scss';

// The conversation on one support ticket, shared by BOTH surfaces: the
// management detail drawer and the reporter's own "My tickets" row. Same
// component, same procedures — the server decides what each caller may reach,
// so there is no second, weaker copy of the access rule on the client.

const formatCommentTime = (iso: string): string =>
  // Stored as UTC wall-clock (timestamp without time zone), so it is formatted
  // as UTC — the canonical util, never a local `new Date()` slice.
  formatUtcDate(iso, 'd MMM yyyy • HH:mm');

const CommentItem = ({ comment }: { comment: SupportTicketComment }) => (
  <li className={css.item}>
    <div className={css.itemHeader}>
      {/* Never colour alone: the author is named, in both directions. */}
      <Text as="span" size="xs" weight="semibold">
        {comment.isOwn ? 'You' : (comment.authorName ?? 'Teammate')}
      </Text>
      <Text as="span" size="xs" variant="muted">
        <time dateTime={comment.createdOn}>
          {formatCommentTime(comment.createdOn)}
        </time>
      </Text>
    </div>
    {/* Rendered as text, never as markup — user input is escaped by React. */}
    <Text className={css.body} size="sm">
      {comment.body}
    </Text>
  </li>
);

export interface TicketConversationProps {
  ticketId: string | null;
  /** Defer the fetch until the thread is actually visible. */
  enabled?: boolean;
}

export const TicketConversation = ({
  ticketId,
  enabled = true,
}: TicketConversationProps) => {
  const labelId = useId();
  const [body, setBody] = useState('');
  const { data, isLoading, isError, refetch } = useTicketComments(
    ticketId,
    enabled
  );
  const addComment = useAddTicketComment();

  // ONE id per submission attempt, reused while the same draft is retried: the
  // server is idempotent on it, so a double-click or a retry after a lost
  // response can never produce two comments (or two alerts). A new id is minted
  // only once the current draft has actually landed.
  const requestIdRef = useRef<string | null>(null);
  const trimmed = body.trim();
  const canSubmit = trimmed.length > 0 && !addComment.isPending && ticketId;

  const clearDraft = () => {
    // Drop the request id with the text: what gets typed next is a NEW comment,
    // and reusing the abandoned draft's key would make the server return that
    // one instead of writing this one.
    requestIdRef.current = null;
    setBody('');
  };

  const submit = () => {
    if (!(canSubmit && ticketId)) return;
    requestIdRef.current ??= crypto.randomUUID();
    addComment.mutate(
      { ticketId, clientRequestId: requestIdRef.current, body: trimmed },
      {
        onSuccess: () => {
          // The comment appearing in the thread IS the success feedback, so no
          // toast; a fresh id is minted for the next draft.
          requestIdRef.current = null;
          setBody('');
        },
        onError: () =>
          toastStore.getState().show({ message: 'Could not add the comment' }),
      }
    );
  };

  const comments = data ?? [];

  return (
    <section aria-labelledby={labelId} className={css.root}>
      <Text className={css.heading} id={labelId} weight="medium">
        Comments
      </Text>

      {isLoading ? (
        <div className={css.list}>
          <Skeleton width="80%" />
          <Skeleton width="60%" />
        </div>
      ) : null}

      {isError ? (
        <div className={css.state}>
          <Text size="sm" variant="muted">
            Could not load the comments.
          </Text>
          <Button onClick={() => refetch()} size="sm" variant="outline">
            Try again
          </Button>
        </div>
      ) : null}

      {!(isLoading || isError) && comments.length === 0 ? (
        <Text className={css.state} size="sm" variant="muted">
          No comments yet.
        </Text>
      ) : null}

      {comments.length > 0 ? (
        // Polite: a reply arriving on a refetch is announced without stealing
        // focus from the composer.
        <ul aria-live="polite" className={css.list}>
          {comments.map((comment) => (
            <CommentItem comment={comment} key={comment.id} />
          ))}
        </ul>
      ) : null}

      <div className={css.composer}>
        <Textarea
          aria-label="Add a comment"
          disabled={addComment.isPending}
          maxLength={SUPPORT_COMMENT_MAX_LENGTH}
          onChange={(event) => setBody(event.target.value)}
          placeholder="Write a comment…"
          rows={3}
          value={body}
        />
        <div className={css.composerActions}>
          <Button
            // Nothing typed → nothing to discard, same as nothing to save.
            disabled={!trimmed || addComment.isPending}
            onClick={clearDraft}
            size="sm"
            variant="ghost"
          >
            Cancel
          </Button>
          <Button
            disabled={!canSubmit}
            onClick={submit}
            size="sm"
            variant="primary"
          >
            {addComment.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </section>
  );
};
